/**
 * CRM Partenariats Fournisseurs — /app/partenariats
 * Pipeline visuel des demandes reçues sur la page /partenariat-fournisseurs.
 * Statuts : nouveau → contacté → négociation → confirmé / refus.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Handshake, EnvelopeSimple, Phone, Globe, Buildings, ArrowSquareOut } from "@phosphor-icons/react";
import { PageHeader, EmptyState } from "../components/ui/kaizen";

const CATEGORY_LABELS = {
  textile: "Textile / vêtements",
  bjj_grappling: "BJJ / Judo / grappling",
  striking: "Kickboxing / Muay Thaï",
  protections: "Protections / arbitrage",
  medailles: "Médailles / trophées",
  autre: "Autre",
};

const STATUSES = [
  { key: "nouveau", label: "Nouveau", color: "sky" },
  { key: "contacte", label: "Contacté", color: "amber" },
  { key: "negociation", label: "Négociation", color: "violet" },
  { key: "confirme", label: "Confirmé", color: "emerald" },
  { key: "refus", label: "Refusé", color: "rose" },
];

const COLOR_CLASS = {
  sky: "border-sky-500/40 text-sky-300 bg-sky-500/5",
  amber: "border-amber-500/40 text-amber-300 bg-amber-500/5",
  violet: "border-violet-500/40 text-violet-300 bg-violet-500/5",
  emerald: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  rose: "border-rose-500/40 text-rose-300 bg-rose-500/5",
};

export default function PartnershipsModule() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/partnership/inquiries");
      setInquiries(r.data.inquiries || []);
    } catch (e) {
      toast.error("Erreur chargement : " + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  async function setStatus(id, status) {
    // optimistic
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await api.patch(`/partnership/inquiries/${id}`, { status });
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error("Erreur — rechargement");
      load();
    }
  }

  const byStatus = useMemo(() => {
    const g = {};
    STATUSES.forEach((s) => (g[s.key] = []));
    inquiries.forEach((i) => {
      const k = i.status && g[i.status] ? i.status : "nouveau";
      g[k].push(i);
    });
    return g;
  }, [inquiries]);

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 pb-12" data-testid="partnerships-module">
      <PageHeader
        title="CRM Partenariats"
        icon={Handshake}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Partenariats" }]}
        subtitle={<>Demandes reçues sur la page publique <a href="/partenariat-fournisseurs" target="_blank" rel="noreferrer" className="text-amber-300 underline">/partenariat-fournisseurs</a></>}
        actions={
          <button
            onClick={load}
            data-testid="partnerships-refresh"
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 font-semibold px-4 py-2 rounded-lg"
          >
            ↻ Rafraîchir
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {STATUSES.map((s) => (
          <div key={s.key} className={`rounded-xl border p-4 ${COLOR_CLASS[s.color]}`} data-testid={`partnerships-stat-${s.key}`}>
            <div className="text-3xl font-bold">{byStatus[s.key]?.length || 0}</div>
            <div className="text-xs uppercase tracking-wider opacity-80">{s.label}</div>
          </div>
        ))}
      </div>

      {loading && <div className="text-zinc-500 text-sm">Chargement…</div>}
      {!loading && inquiries.length === 0 && (
        <EmptyState
          icon={Handshake}
          title="Aucune demande de partenariat"
          description="Les demandes apparaîtront ici dès qu'un fournisseur soumet le formulaire sur la page publique."
        />
      )}

      {/* Kanban */}
      {!loading && inquiries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {STATUSES.map((s) => (
            <div key={s.key} className="space-y-3" data-testid={`partnerships-col-${s.key}`}>
              <div className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg border ${COLOR_CLASS[s.color]}`}>
                {s.label} · {byStatus[s.key]?.length || 0}
              </div>
              {byStatus[s.key]?.map((it) => (
                <InquiryCard key={it.id} inquiry={it} onStatus={setStatus} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const InquiryCard = ({ inquiry, onStatus }) => (
  <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950" data-testid={`partnerships-card-${inquiry.id}`}>
    <div className="flex items-start gap-2 mb-2">
      <Buildings size={18} className="text-amber-400 shrink-0 mt-0.5" />
      <div className="font-semibold text-zinc-100 leading-tight">{inquiry.company_name}</div>
    </div>
    <div className="text-xs text-zinc-500 mb-3">{CATEGORY_LABELS[inquiry.category] || inquiry.category}</div>

    <div className="space-y-1.5 text-xs text-zinc-300 mb-3">
      <div className="text-zinc-400">{inquiry.contact_name}</div>
      <a href={`mailto:${inquiry.email}`} className="flex items-center gap-1.5 hover:text-amber-300">
        <EnvelopeSimple size={13} /> {inquiry.email}
      </a>
      {inquiry.phone && (
        <a href={`tel:${inquiry.phone}`} className="flex items-center gap-1.5 hover:text-amber-300">
          <Phone size={13} /> {inquiry.phone}
        </a>
      )}
      {inquiry.website && (
        <a href={inquiry.website.startsWith("http") ? inquiry.website : `https://${inquiry.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-amber-300">
          <Globe size={13} /> {inquiry.website.replace(/^https?:\/\//, "")} <ArrowSquareOut size={11} />
        </a>
      )}
    </div>

    {inquiry.message && (
      <div className="text-xs text-zinc-400 bg-black border border-zinc-900 rounded-lg p-2.5 mb-3 max-h-28 overflow-auto whitespace-pre-wrap">
        {inquiry.message}
      </div>
    )}

    <div className="text-[10px] text-zinc-600 mb-2">
      {(inquiry.created_at || "").slice(0, 10)} · réf KZN-PT-{(inquiry.id || "").slice(0, 8).toUpperCase()}
    </div>

    <select
      value={inquiry.status || "nouveau"}
      onChange={(e) => onStatus(inquiry.id, e.target.value)}
      data-testid={`partnerships-status-select-${inquiry.id}`}
      className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs rounded-lg px-2 py-1.5 focus:border-amber-500 outline-none"
    >
      {STATUSES.map((s) => (
        <option key={s.key} value={s.key}>{s.label}</option>
      ))}
    </select>
  </div>
);
